import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Lock } from 'lucide-react';
import { EducateNavHeader } from '@/components/brand/EducateNavHeader';
import { FlashCard } from '@/components/brand/FlashCard';
import { Question } from '@/components/quiz/Question';
import { OptionState } from '@/components/quiz/types';
import type { EducateModule } from '@/hooks/useEducateModule';
import type { EducateUnit } from '@/utils/units';
import type { EducateInteraction, QuizQuestion } from '@/types/educate';
import { loadPlayerProgress, savePlayerProgress } from '@/utils/playerProgress';

type ReadingUnit = Extract<EducateUnit, { kind: 'reading' }>;
type SectionStatus = 'done' | 'active' | 'locked';

interface ConceptSaved {
  answers: Record<string, EducateInteraction>;
}

export function ConceptWorkbook({ module }: { module: EducateModule }) {
  const { storyline, units, currentIndex, completedUnits, advance } = module;
  const sections = units.filter((unit): unit is ReadingUnit => unit.kind === 'reading');
  const total = sections.length;
  const doneCount = sections.filter((section) => completedUnits.includes(section.id)).length;
  const showLogo = storyline?.uiContent.uiConfig?.showKnolskapeLogo ?? true;

  const saved = useMemo(
    () => loadPlayerProgress<ConceptSaved>(storyline?.storylineId),
    [storyline?.storylineId]
  );
  const [answers, setAnswers] = useState<Record<string, EducateInteraction>>(() =>
    saved.answers && typeof saved.answers === 'object' ? saved.answers : {}
  );
  const recordCorrect = useCallback(
    (interaction: EducateInteraction) =>
      setAnswers((prev) => ({ ...prev, [interaction.unitId]: interaction })),
    []
  );

  useEffect(() => {
    savePlayerProgress<ConceptSaved>(storyline?.storylineId, { answers });
  }, [storyline?.storylineId, answers]);

  const advancedFor = useRef<string | null>(null);
  useEffect(() => {
    const active = sections[currentIndex];
    if (!active || completedUnits.includes(active.id) || active.quiz.length === 0) return;

    const correct = active.quiz.filter(
      (_, index) => answers[`${active.id}-quiz-${index}`]?.isCorrect
    ).length;

    if (correct === active.quiz.length && advancedFor.current !== active.id) {
      advancedFor.current = active.id;
      const interactions = active.quiz
        .map((_, index) => answers[`${active.id}-quiz-${index}`])
        .filter(Boolean);
      advance(interactions);
    }
  }, [answers, currentIndex, completedUnits, sections, advance]);

  const activeRef = useRef<HTMLElement | null>(null);
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current)
      activeRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    else mounted.current = true;
  }, [currentIndex]);

  return (
    <EducateNavHeader
      eyebrow="Micro Workbook"
      title={storyline?.uiContent.title ?? ''}
      showLogo={showLogo}
    >
      <main className="w-full flex-1 px-[18%] py-8" data-testid="workbook-screen">
        <div className="mb-5 flex items-center justify-between gap-3">
          <h2 className="text-2xl font-bold text-slate-800">{storyline?.uiContent.title ?? ''}</h2>
          <span className="shrink-0 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {doneCount} of {total} sections complete
          </span>
        </div>

        <div className="space-y-5">
          {sections.map((section, index) => {
            const status: SectionStatus = completedUnits.includes(section.id)
              ? 'done'
              : index === currentIndex
                ? 'active'
                : 'locked';

            return (
              <SectionCard
                key={section.id}
                ref={status === 'active' ? activeRef : undefined}
                section={section}
                number={index + 1}
                status={status}
                isLast={index === total - 1}
                answers={answers}
                onRecordCorrect={recordCorrect}
                onManualComplete={() => advance([])}
              />
            );
          })}
        </div>
      </main>
    </EducateNavHeader>
  );
}

interface SectionCardProps {
  section: ReadingUnit;
  number: number;
  status: SectionStatus;
  isLast: boolean;
  answers: Record<string, EducateInteraction>;
  onRecordCorrect: (interaction: EducateInteraction) => void;
  onManualComplete: () => void;
}

const SectionCard = forwardRef<HTMLElement, SectionCardProps>(function SectionCard(
  { section, number, status, isLast, answers, onRecordCorrect, onManualComplete },
  ref
) {
  const locked = status === 'locked';

  return (
    <section
      ref={ref}
      aria-disabled={locked}
      className={`scroll-mt-24 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition ${
        locked ? 'opacity-45' : ''
      }`}
    >
      <header className="flex items-center gap-3">
        <SectionBadge number={number} status={status} />
        <h3 className="text-xl font-bold text-slate-800">{section.title}</h3>
      </header>

      {locked ? (
        <p className="mt-4 flex items-center gap-1.5 text-base text-slate-400">
          <Lock size={16} /> Complete the previous section to unlock this one.
        </p>
      ) : (
        <>
          <div className="mt-4 space-y-3">
            {section.paragraphs.map((paragraph, index) => (
              <p key={index} className="whitespace-pre-line text-lg leading-relaxed text-slate-700">
                {paragraph}
              </p>
            ))}
          </div>

          {section.flashcards.length > 0 && (
            <div className="mt-6">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                Key concepts
              </p>
              <div className="mx-auto mt-2 grid max-w-2xl gap-4 sm:grid-cols-2">
                {section.flashcards.map((card) => (
                  <FlashCard key={card.term} term={card.term} definition={card.definition} />
                ))}
              </div>
            </div>
          )}

          {section.quiz.length > 0 && (
            <div className="mt-6 border-t-2 border-slate-200 pt-5">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                Knowledge check
              </p>
              <div className="mt-3 space-y-6">
                {section.quiz.map((question, index) => {
                  const answerId = `${section.id}-quiz-${index}`;
                  const savedAnswer = answers[answerId]?.answer;
                  return (
                    <KnowledgeCheck
                      key={index}
                      answerId={answerId}
                      question={question}
                      disabled={status !== 'active'}
                      initialSolved={typeof savedAnswer === 'number' ? savedAnswer : null}
                      onCorrect={onRecordCorrect}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {status === 'active' && section.quiz.length === 0 && (
            <button
              type="button"
              onClick={onManualComplete}
              className="mt-6 rounded-lg bg-brand-600 px-4 py-2.5 text-base font-semibold text-white transition hover:bg-brand-700"
            >
              Mark section complete
            </button>
          )}
        </>
      )}

      {status === 'done' && !isLast && (
        <p className="mt-6 rounded-lg bg-brand-50 px-4 py-3 text-center text-base font-semibold text-brand-700">
          ✓ Section {number} complete — next section unlocked!
        </p>
      )}
    </section>
  );
});

function SectionBadge({ number, status }: { number: number; status: SectionStatus }) {
  const tone =
    status === 'done'
      ? 'bg-emerald-500 text-white'
      : status === 'active'
        ? 'bg-amber-400 text-slate-900'
        : 'bg-slate-200 text-slate-500';

  return (
    <span
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${tone}`}
    >
      {status === 'done' ? <Check size={14} /> : number}
    </span>
  );
}

interface KnowledgeCheckProps {
  answerId: string;
  question: QuizQuestion;
  disabled: boolean;
  initialSolved?: number | null;
  onCorrect: (interaction: EducateInteraction) => void;
}

const WRONG_FLASH_MS = 900;

const WORKBOOK_QUESTION_CSS = {
  title: 'text-lg font-semibold text-slate-800',
  option: {
    base: 'flex w-full items-center gap-3 rounded-lg border px-3.5 py-3 text-left text-base transition-colors duration-1000 ease-out disabled:cursor-default',
    letter:
      'flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-bold transition-colors duration-1000 ease-out'
  }
};

function KnowledgeCheck({
  answerId,
  question,
  disabled,
  initialSolved = null,
  onCorrect
}: KnowledgeCheckProps) {
  const [wrong, setWrong] = useState<number | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [solved, setSolved] = useState<number | null>(initialSolved);
  const isSolved = solved !== null;

  useEffect(() => {
    if (wrong === null) return;
    const timer = setTimeout(() => setWrong(null), WRONG_FLASH_MS);
    return () => clearTimeout(timer);
  }, [wrong, attempt]);

  const choose = (option: number) => {
    if (disabled || isSolved) return;
    if (option === question.correctIndex) {
      setWrong(null);
      setSolved(option);
      onCorrect({ unitId: answerId, kind: 'quiz', answer: option, isCorrect: true });
    } else {
      setWrong(option);
      setAttempt((n) => n + 1);
    }
  };

  const optionState = (index: number): OptionState =>
    isSolved && index === solved
      ? OptionState.CORRECT
      : index === wrong
        ? OptionState.INCORRECT
        : isSolved
          ? OptionState.MUTED
          : OptionState.DEFAULT;

  return (
    <Question
      question={question.question}
      options={question.options}
      optionState={optionState}
      onSelect={choose}
      isDisabled={() => disabled || isSolved}
      css={WORKBOOK_QUESTION_CSS}
      feedback={
        wrong !== null && !isSolved ? (
          <p className="mt-2 text-sm font-semibold text-red-500">Incorrect — try again</p>
        ) : null
      }
    />
  );
}
